<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";
import { useRouter } from "vue-router";

import actions from "~/actions";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import EntityDropdownItem from "~/components/dumb/EntityDropdownItem.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import { DROPDOWN_REGISTER_SUBDROPDOWN_STATE } from "~/constants/injection";
import { TaskIcon } from "~/icons";
import { Placement } from "~/shared/enums";
import type { Doc, Task } from "~/shared/types";
import { useDataStore, usePageStore, useUserStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { getTaskLink } from "~/utils/common";
import { sign } from "~/utils/comparator";
import { useAsyncTasks, usePaginatedTasksWithSearch } from "~/utils/entityIncrementalLoading";

const props = defineProps<{
  doc: Doc;
  itemIndex?: number;
}>();

const router = useRouter();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const menu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);
const scrollContainer = computed(() => menu.value?.scrollContainer ?? null);

const isOpen = computed(() => menu.value?.isOpen ?? false);

const registerSubdropdownState = inject(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, null);

watch(isOpen, (newValue) => {
  if (registerSubdropdownState && props.itemIndex !== undefined) {
    registerSubdropdownState(props.itemIndex, newValue);
  }
});

const search = ref("");
const pageDuid = computed(() => `task-dropdown-${search.value}`);

const { searchedTasks, isLoading, reset: resetTasks } = usePaginatedTasksWithSearch(pageDuid, scrollContainer, search);

const { tasks: recentTasks } = useAsyncTasks(
  computed(() => userStore.recentTaskStack),
  computed(() => ({ includeTrashed: false, includeDraft: false }))
);

const editing = ref(false);

const taskRelationships = computed(() => dataStore.getTaskDuidsRelatedToDoc(props.doc));
const { state: relatedTasks } = asyncState(() => dataStore.getTasksRelatedToDocOrdered(props.doc), {
  default: dataStore.getTasksRelatedToDocOrderedIfLoaded(props.doc),
  watch: [() => props.doc.duid, taskRelationships],
});

const onSelect = async (taskDuid: string) => {
  const task = await dataStore.getTaskByDuid(taskDuid);
  if (!task) {
    return;
  }

  await dataStore.createTaskDocRelationship(task.duid, props.doc.duid);
  relatedTasks.value = await dataStore.getTasksRelatedToDocOrdered(props.doc);
};

const openMenu = () => {
  editing.value = true;
  nextTick(() => {
    menu.value?.open?.();
  });
};

const removeTask = async (taskDuid: string) => {
  await dataStore.deleteTaskDocRelationship(taskDuid, props.doc.duid);
  relatedTasks.value = await dataStore.getTasksRelatedToDocOrdered(props.doc);
};

const openTask = (task: Task) => {
  router.push(getTaskLink(task));
};

const onSearchTasks = (value: string) => {
  search.value = value;
  searchedTasks.value = [];
  resetTasks();
};

const createTaskItem = (task: Task, customKey?: string) => ({
  value: task.duid,
  label: task.title,
  selected: relatedTasks.value.some((e) => e.duid === task.duid),
  disabled: false,
  customKey,
  component: EntityDropdownItem,
  componentArgs: {
    showOpen: true,
    onOpen: () => openTask(task),
  },
});

const taskSections = computed(() => {
  const recentTaskCount = userStore.recentTaskStack.length;

  const recentSection =
    userStore.recentTaskStack.length > 0 && recentTasks.value.length > 0
      ? [
          {
            title: "Recent",
            showTitle: true,
            items: recentTasks.value.map((task) => createTaskItem(task, `recent/${task.duid}`)),
          },
        ]
      : [];

  const sortedEntries = actions.task.groupAndSortTasksByDartboard(searchedTasks.value);
  const regularSections = sortedEntries.map(([title, tasks]) => ({
    title,
    showTitle: true,
    items: tasks
      .sort((a, b) => {
        const aIndex = userStore.recentTaskStack.indexOf(a.duid);
        const bIndex = userStore.recentTaskStack.indexOf(b.duid);
        return sign((aIndex > -1 ? aIndex : recentTaskCount) - (bIndex > -1 ? bIndex : recentTaskCount));
      })
      .map((task) => createTaskItem(task)),
  }));

  return [...recentSection, ...regularSections];
});

defineExpose({
  openMenu,
});
</script>

<template>
  <MultiselectDropdownMenu
    ref="menu"
    :sections="taskSections"
    header-title="Link a task"
    :distance="!pageStore.isMobile ? 2 : undefined"
    :placement="pageStore.isMobile ? Placement.RIGHT_TOP : undefined"
    placeholder="Link a task..."
    :width-pixels="448"
    :on-after-close="
      () => {
        editing = false;
      }
    "
    infinite-list
    :loading-more="isLoading"
    @search="onSearchTasks"
    @add="onSelect"
    @remove="removeTask">
    <DropdownMenuItemContent
      :icon="TaskIcon"
      title="Link a task"
      :right-text="relatedTasks.length > 0 ? relatedTasks.length : undefined"
      right-text-style="text-xs text-vlt"
      is-submenu />
  </MultiselectDropdownMenu>
</template>
